import React, { useCallback } from 'react';
import { Box, Skeleton, Card, CardContent, useMediaQuery, useTheme } from '@mui/material';
import { VirtuosoGrid } from 'react-virtuoso';
import FileCard from './FileCard';

function useGridComponents() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const colWidth = isMobile ? 160 : 340;
  const gap = isMobile ? 1 : 2;

  return {
    colWidth,
    gap,
    components: {
      List: React.forwardRef(({ style, children, ...props }, ref) => (
        <Box
          ref={ref}
          {...props}
          style={style}
          sx={{
            display: 'grid',
            gridTemplateColumns: `repeat(auto-fill, minmax(${colWidth}px, 1fr))`,
            gap,
            px: 0,
          }}
        >
          {children}
        </Box>
      )),
      Item: ({ children, ...props }) => (
        <Box {...props} sx={{ display: 'flex' }}>
          {children}
        </Box>
      ),
    },
  };
}

function LoadingSkeleton() {
  return (
    <Card>
      <CardContent>
        <Box sx={{ display: 'flex', gap: 1.5 }}>
          <Skeleton variant="rounded" width={40} height={40} />
          <Box sx={{ flex: 1 }}>
            <Skeleton width="70%" height={24} />
            <Skeleton width="40%" height={16} />
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 0.5, mt: 1.5 }}>
          <Skeleton variant="rounded" width={60} height={24} />
          <Skeleton variant="rounded" width={80} height={24} />
        </Box>
      </CardContent>
    </Card>
  );
}

export default function FileGrid({
  files,
  hasNextPage,
  fetchNextPage,
  isFetchingNextPage,
  isLoading,
  onQRClick,
  onDelete,
  onDownload,
  onCopyLink,
  onContextMenu,
  selectionMode,
  selectedIds,
  onToggleSelect,
  search,
  onFileClick,
}) {
  const loadMore = useCallback(() => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  const { colWidth, gap, components: gridComponents } = useGridComponents();

  if (isLoading) {
    return (
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: `repeat(auto-fill, minmax(${colWidth}px, 1fr))`,
          gap,
        }}
      >
        {Array.from({ length: 6 }).map((_, i) => (
          <LoadingSkeleton key={i} />
        ))}
      </Box>
    );
  }

  return (
    <VirtuosoGrid
      useWindowScroll
      data={files}
      components={gridComponents}
      endReached={loadMore}
      overscan={200}
      itemContent={(index, file) => (
        <FileCard
          file={file}
          onQRClick={onQRClick}
          onDelete={onDelete}
          onDownload={onDownload}
          onCopyLink={onCopyLink}
          onContextMenu={onContextMenu}
          selectionMode={selectionMode}
          selected={selectedIds?.has(file.id)}
          onToggleSelect={onToggleSelect}
          search={search}
          onFileClick={onFileClick}
        />
      )}
    />
  );
}
